"use client";

/**
 * Multi-select card list used for graph-driven prompt responses.
 *
 * Chrome and description placement live in IntakeOptionCard; this component
 * only maps a checkbox-list item onto it.
 */

import type { ReactNode } from "react";
import { IntakeOptionCard } from "./IntakeOptionCard";

export interface CheckboxListItem {
  id: string;
  name: string;
  description?: string;
  /** Product names shown as "Addressed by: …" beneath the description. Path A only. */
  matchingProducts?: string[];
}

export interface IntakeCheckboxListProps {
  items: CheckboxListItem[];
  selectedIds: string[];
  /** Called with the id of the item that was clicked. */
  onToggle: (id: string) => void;
  /** Accessible name for the checkbox group. */
  ariaLabel: string;
  /** Rendered inside the group after the cards — the refine row. */
  trailing?: ReactNode;
}

export function IntakeCheckboxList({
  items,
  selectedIds,
  onToggle,
  ariaLabel,
  trailing,
}: IntakeCheckboxListProps) {
  return (
    // biome-ignore lint/a11y/useSemanticElements: a neutral visual group avoids fieldset chrome
    <div role="group" aria-label={ariaLabel} className="flex flex-col gap-2">
      {items.map((item) => (
        <IntakeOptionCard
          key={item.id}
          name={item.name}
          description={item.description}
          indicator="checkbox"
          selected={selectedIds.includes(item.id)}
          onSelect={() => onToggle(item.id)}
          footer={
            item.matchingProducts && item.matchingProducts.length > 0 ? (
              <>
                <span className="mt-2 block border-t border-(--border-subtle)" />
                <span className="mt-2 text-[12.5px] text-(--fg-muted)">
                  Addressed by:{" "}
                  <span className="font-semibold text-(--fg-secondary)">
                    {item.matchingProducts.join(", ")}
                  </span>
                </span>
              </>
            ) : undefined
          }
        />
      ))}
      {trailing}
    </div>
  );
}
